import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useBlogStore from "../stores/blogs/blogs";
import { useToast } from "../context/toastContext";
import ConfirmationModal from "../components/modal/ConfirmationModal";
import { useState } from "react";

const ArticleManagement = () => {
  const navigate = useNavigate();
  const language = window.location.pathname.startsWith("/kr") ? "kr" : "en";

  const { addToast } = useToast();

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSlug, setPendingSlug] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const articles = useBlogStore((s) => s.articles);
  const loadingList = useBlogStore((s) => s.loadingList);
  const { fetchList, deleteBlog } = useBlogStore((s) => s.actions);

  useEffect(() => {
    fetchList({ language, page: 1, size: 20 });
  }, [language]);

  const handleDelete = (slug) => {
    setPendingSlug(slug);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!pendingSlug) return;

    setConfirmLoading(true);

    const ok = await deleteBlog(pendingSlug, language);

    setConfirmLoading(false);
    setShowConfirm(false);
    setPendingSlug(null);

    if (ok) {
      addToast("success", "Article deleted.");
      fetchList({ language, page: 1, size: 20 }); // refresh table
    } else {
      addToast("error", "Failed to delete article.");
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm min-h-screen">
      <ConfirmationModal
        show={showConfirm}
        title="Delete Article"
        message="This action cannot be undone. Do you really want to delete this article?"
        confirmText="Delete"
        cancelText="Cancel"
        isProcessing={confirmLoading}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirm(false);
          setPendingSlug(null);
        }}
      />

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-semibold text-gray-800">
          Articles Management
        </h1>

        {/* ACTION BUTTONS */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/article/category")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
          >
            Manage Categories
          </button>

          <button
            onClick={() => navigate("/article/create")}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm"
          >
            Create Article
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          {/* TABLE HEAD */}
          <thead className="bg-gray-50">
            <tr>
              {["Title", "Category", "Excerpt", "Views", "Actions"].map(
                (text) => (
                  <th
                    key={text}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    {text}
                  </th>
                )
              )}
            </tr>
          </thead>

          {/* TABLE BODY */}
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Skeleton Loading */}
            {loadingList &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-10"></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                  </td>
                </tr>
              ))}

            {/* NORMAL ROWS */}
            {!loadingList &&
              articles.map((article) => (
                <tr key={article.slug} className="hover:bg-gray-50 transition">
                  <td className="text-left px-4 py-3 text-sm font-medium text-gray-900">
                    {article.title}
                  </td>

                  <td className="text-left px-4 py-3 text-sm text-gray-700">
                    {article.categories?.length
                      ? article.categories.join(", ")
                      : article.category}
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                    {article.excerpt}
                  </td>

                  <td className="px-4 py-3 text-sm text-gray-600">
                    {article.viewCount ?? 0}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          navigate(`/article/edit/${article.slug}`)
                        }
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-xs"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(article.slug);
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* FOOTER (OPTIONAL PAGINATION FUTURE USE) */}
      <div className="mt-4 text-sm text-gray-600">
        Showing {articles.length} articles
      </div>
    </div>
  );
};

export default ArticleManagement;
